import { Spinner } from "@fluentui/react-components";
import {
  getCategories,
  getConversationsToReference,
  getUseCasePrompt,
  getUseCases,
  getWorkspaceLibraryDocuments,
} from "api";
import { ConstantValues } from "common/constants";
import { AnnouncementsBanner } from "components/manageAnnouncements/announcementsBanner/AnnouncementsBanner";
import { IWorkspaceLibraryDocument } from "components/manageWorkspaces/workspaceLibrary/workspaceLibrary.types";
import { useCategories } from "hooks/useCategories";
import { useResizer } from "hooks/useResizer";
import { LLMType } from "models/enums/LLMType";
import { ICategory } from "models/ICategory";
import { IConversation } from "models/IConversation";
import { IFile } from "models/IFile";
import { IMessage } from "models/IMessage";
import { ITag } from "models/ITag";
import { ITemplate } from "models/ITemplate";
import { IUseCase } from "models/IUseCase";
import { IWorkspace } from "models/IWorkspace";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { stripHtmlTags } from "utils/generalUtils";
import { resolveRecommendedModel } from "utils/llmModelMigration";
import { AllowedRagCategory, setNewConfigForKey } from "../../redux/llmConfig";
import { setReferencedConversations } from "../../redux/referencedConversationsSlice";
import {
  resetCurrentPrompt,
  setCurrentPrompt,
} from "../../redux/selectedPrompt";
import { setTagsForKey } from "../../redux/tags";
import {
  addLibraryDocumentForKey,
  clearLibraryDocumentsForKey,
} from "../../redux/workspaceLibraryDocuments";
import { useChatStyles } from "./Chat.styles";
import { ConversationMessages } from "./conversationMessages/ConversationMessages";
import { ManageContent } from "./manageContent/ManageContent";
import { MessageBar } from "./messageBar/MessageBar";
import { TemplateGalleryModal } from "./quickStartGallery/TemplateGalleryModal";
import { TopBar } from "./topBar/TopBar";

export const Chat = (): JSX.Element => {
  const isMobile = useResizer();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState<boolean>(true);
  const [isLoadingPromptFromUrl, setIsLoadingPromptFromUrl] =
    useState<boolean>(false);
  const styles = useChatStyles();
  const dispatch = useDispatch();
  const accessModels = useSelector(
    (s: RootState) => s.accessLLMModelsSlice.value,
  );
  const currentUser = useSelector((s: RootState) => s.currentUserSlice.value);
  const [isEditMessageActive, setIsEditMessageActive] =
    useState<boolean>(false);
  const [promptToEdit, setPromptToEdit] = useState<string | null>(null);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState<boolean>(false);
  const { data: categories = [] } = useCategories();

  const kcLookup = useMemo(() => {
    const map = new Map<string, ITag>();
    categories.forEach((cat: any) => {
      cat.tags.forEach((tag: ITag) => {
        if (tag.id) map.set(tag.id, tag);
      });
    });
    return map;
  }, [categories]);

  const handleUseTemplate = useCallback(
    async (template: ITemplate) => {
      try {
        // Resolves by display name or name; a Sonnet 4.5 recommendation moves
        // to 5.5 only when the user can see 5.5.
        const templateModel = resolveRecommendedModel(
          accessModels,
          template.recommendedLLM,
        );

        const ragConfig = {
          rag: template.qualsEnabled || template.proposalsEnabled,
          citate: template.citationsEnabled,
          sourceRagCategories: [
            ...(template.qualsEnabled
              ? ["Qualifications" as AllowedRagCategory]
              : []),
            ...(template.proposalsEnabled
              ? ["Proposals" as AllowedRagCategory]
              : []),
          ],
        };

        if (templateModel) {
          const nextLlm = (templateModel.name ?? LLMType.CLAUDE).toLowerCase();
          try {
            localStorage.setItem(
              "userLLMSettings",
              JSON.stringify({
                userId: currentUser.id,
                llmType: nextLlm,
                rag: ragConfig.rag,
                citate: ragConfig.citate,
              }),
            );
          } catch (e) {
            console.error("Error saving user LLM settings:", e);
          }
          dispatch(
            setNewConfigForKey({
              key: ConstantValues.CHAT,
              config: { llmType: nextLlm, ...ragConfig },
            }),
          );
        } else {
          dispatch(
            setNewConfigForKey({
              key: ConstantValues.CHAT,
              config: ragConfig,
            }),
          );
        }

        if (
          template.suggestedKnowledgeComponents &&
          template.suggestedKnowledgeComponents.length > 0
        ) {
          const preselectedKCs = template.suggestedKnowledgeComponents
            .map((kcId) => kcLookup.get(kcId))
            .filter((kc): kc is ITag => !!kc);

          if (preselectedKCs.length > 0) {
            dispatch(
              setTagsForKey({
                key: ConstantValues.CHAT,
                tags: preselectedKCs,
              }),
            );
          }
        }

        if (template.prefilledPrompt) {
          dispatch(
            setCurrentPrompt({
              key: ConstantValues.CHAT,
              prompt: {
                id: template.id,
                description: template.prefilledPrompt,
                title: template.name,
              },
            }),
          );
        } else {
          dispatch(resetCurrentPrompt({ key: ConstantValues.CHAT }));
        }

        setIsGalleryModalOpen(false);
      } catch (error) {
        console.error("Error applying template:", error);
      }
    },
    [accessModels, kcLookup, dispatch, currentUser.id],
  );

  const [isManageContentOpen, setIsManageContentOpen] =
    useState<boolean>(false);
  const [allKnowledgeComponents, setAllKnowledgeComponents] = useState<ITag[]>(
    [],
  );
  const [availableConversations, setAvailableConversations] = useState<
    IConversation[]
  >([]);
  const [allWorkspaceDocuments, setAllWorkspaceDocuments] = useState<
    IWorkspaceLibraryDocument[]
  >([]);
  const [isLoadingKnowledgeComponents, setIsLoadingKnowledgeComponents] =
    useState<boolean>(false);
  const [isLoadingWorkspaceDocuments, setIsLoadingWorkspaceDocuments] =
    useState<boolean>(false);
  const [isLoadingConversations, setIsLoadingConversations] =
    useState<boolean>(false);

  const [isPersonalWorkspace, setIsPersonalWorkspace] =
    useState<boolean>(false);

  const activeWorkspace: IWorkspace | null = useSelector(
    (state: RootState) => state.activeWorkspaceSlice.value,
  );
  const currentConversation = useSelector(
    (state: RootState) => state.currentConversationSetter.value,
  );
  const conversationKey = currentConversation?.id || ConstantValues.CHAT;
  const currentTags: ITag[] = useSelector(
    (state: RootState) => state.currentTagsSlice.tags[conversationKey] || [],
  );
  const currentLibraryDocuments: IWorkspaceLibraryDocument[] = useSelector(
    (state: RootState) =>
      state.workspaceLibraryDocumentsSlice.documents[conversationKey] || [],
  );
  const currentMessages: IMessage[] = useSelector(
    (state: RootState) => state.currentMessagesSetter.value,
  );
  const referencedConversations: IConversation[] = useSelector(
    (state: RootState) =>
      state.referencedConversationsSlice.conversations || [],
  );

  const documentOptions: IFile[] = currentMessages
    .flatMap((message) => message.files || [])
    .filter(
      (file, index, self) =>
        index === self.findIndex((f) => f.filename === file.filename),
    );

  useEffect(() => {
    const searchParams: URLSearchParams = new URLSearchParams(
      window.location.search,
    );
    const promptId: string | null = searchParams.get("promptId");
    const useCaseId: string | null = searchParams.get("useCaseId");
    if (!promptId || !useCaseId) {
      return;
    }

    const preparePrompt = async () => {
      setIsLoadingPromptFromUrl(true);
      try {
        const [useCases, prompt] = await Promise.all([
          getUseCases(),
          getUseCasePrompt({ promptId }),
        ]);
        const useCase = useCases.find((uc: IUseCase) => uc.id === useCaseId);
        if (!useCase || !prompt) {
          return;
        }

        const firstByOrder = [...accessModels]
          .filter((m) => typeof m.order === "number")
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];

        const nextLlm = (
          prompt.llmModel ??
          firstByOrder?.name ??
          LLMType.CLAUDE
        ).toLowerCase();

        const nextRagEnabled: boolean =
          (!!prompt.qualsEnabled || !!prompt.proposalsEnabled) ?? false;
        const nextCitateEnabled: boolean = !!prompt.citationsEnabled;
        const nextCategories: Array<AllowedRagCategory> = [];
        if (prompt.qualsEnabled)
          nextCategories.push("Qualifications" as AllowedRagCategory);
        if (prompt.proposalsEnabled)
          nextCategories.push("Proposals" as AllowedRagCategory);

        if (
          prompt.preselectedKnowledgeComponents &&
          prompt.preselectedKnowledgeComponents.length > 0 &&
          categories.length > 0
        ) {
          const preselectedTags: ITag[] = [];
          for (const tagId of prompt.preselectedKnowledgeComponents) {
            for (const category of categories) {
              const tag = category.tags.find((t: ITag) => t.id === tagId);
              if (tag) {
                preselectedTags.push(tag);
                break;
              }
            }
          }

          if (preselectedTags.length > 0) {
            dispatch(
              setTagsForKey({
                key: ConstantValues.CHAT,
                tags: preselectedTags,
              }),
            );
          }
        }

        dispatch(
          setNewConfigForKey({
            key: ConstantValues.CHAT,
            config: {
              llmType: nextLlm,
              rag: nextRagEnabled,
              citate: nextCitateEnabled,
              sourceRagCategories: nextCategories,
            },
          }),
        );

        try {
          localStorage.setItem(
            "userLLMSettings",
            JSON.stringify({
              userId: currentUser.id,
              llmType: nextLlm,
              rag: nextRagEnabled,
              citate: nextCitateEnabled,
            }),
          );
        } catch (e) {
          console.error("Error saving user LLM settings to localStorage:", e);
        }

        dispatch(
          setCurrentPrompt({
            key: ConstantValues.CHAT,
            prompt: {
              id: ConstantValues.EMPTY_STRING,
              description: stripHtmlTags(prompt.prompt),
              title: stripHtmlTags(prompt.title),
            },
          }),
        );

        const url = new URL(window.location.href);
        url.searchParams.delete("promptId");
        url.searchParams.delete("useCaseId");
        window.history.replaceState(
          {},
          ConstantValues.EMPTY_STRING,
          url.toString(),
        );
      } catch (error) {
        console.error("Error applying shared prompt:", error);
      } finally {
        setIsLoadingPromptFromUrl(false);
      }
    };

    preparePrompt();
  }, [dispatch, accessModels, currentUser.id]);

  useEffect(() => {
    if (
      (!referencedConversations || referencedConversations.length === 0) &&
      currentMessages &&
      currentMessages.length > 0
    ) {
      const lastMessageWithRefs = currentMessages.at(-1);

      if (lastMessageWithRefs?.referencedWorkspaceConversations?.length) {
        const restoredConversations: IConversation[] =
          lastMessageWithRefs.referencedWorkspaceConversations
            .map((ref) => {
              const fullConversation = availableConversations.find(
                (c) => c.id === ref.id,
              );
              if (fullConversation) {
                return fullConversation;
              }
              return {
                id: ref.id,
                title: ref.title,
                userId: currentUser?.id || "",
                createdAt: Date.now(),
                updatedAt: Date.now(),
                isFavorited: false,
                workspaceId: activeWorkspace?.id,
              } as IConversation;
            })
            .filter(Boolean);

        if (restoredConversations.length > 0) {
          dispatch(setReferencedConversations(restoredConversations));
        }
      }
    }
  }, [currentMessages]);

  useEffect(() => {
    const fetchKnowledgeComponents = async () => {
      setIsLoadingKnowledgeComponents(true);
      try {
        const categories = await getCategories();
        const allTags: ITag[] = [];
        categories?.forEach((category: ICategory) => {
          if (category.tags) {
            category.tags.forEach((tag: ITag) => {
              allTags.push({
                ...tag,
                category: category,
              });
            });
          }
        });
        setAllKnowledgeComponents(
          allTags.filter((tag) => tag.isPublic === true),
        );
      } catch (error) {
        console.error("Failed to fetch knowledge components:", error);
      } finally {
        setIsLoadingKnowledgeComponents(false);
      }
    };
    fetchKnowledgeComponents();
  }, [currentUser?.id]);

  useEffect(() => {
    const fetchConversations = async () => {
      if (
        activeWorkspace?.id &&
        activeWorkspace.name !== ConstantValues.PERSONAL_WORKSPACE
      ) {
        setIsLoadingConversations(true);
        try {
          const conversations = await getConversationsToReference({
            workspaceId: activeWorkspace.id,
          });
          setAvailableConversations(
            conversations.filter(
              (c: IConversation) => c.id !== conversationKey,
            ) || [],
          );
        } catch (error) {
          console.error("Failed to fetch conversations:", error);
        } finally {
          setIsLoadingConversations(false);
        }
      }
    };
    fetchConversations();
  }, [activeWorkspace, conversationKey]);

  const fetchWorkspaceDocuments = async () => {
    if (
      activeWorkspace?.id &&
      activeWorkspace.name !== ConstantValues.PERSONAL_WORKSPACE
    ) {
      setIsLoadingWorkspaceDocuments(true);
      try {
        const docs = await getWorkspaceLibraryDocuments({
          workspaceId: activeWorkspace.id,
        });
        setAllWorkspaceDocuments(docs || []);
      } catch (error) {
        console.error("Failed to fetch workspace documents:", error);
      } finally {
        setIsLoadingWorkspaceDocuments(false);
      }
    } else {
      setAllWorkspaceDocuments([]);
    }
  };

  useEffect(() => {
    fetchWorkspaceDocuments();

    if (
      activeWorkspace &&
      activeWorkspace.name === ConstantValues.PERSONAL_WORKSPACE
    )
      setIsPersonalWorkspace(true);
    else {
      setIsPersonalWorkspace(false);
    }
  }, [activeWorkspace]);

  return (
    <div className={styles.chat}>
      {isLoadingPromptFromUrl && (
        <div className={styles.loadingOverlay}>
          <Spinner size="extra-large" />
          <div className={styles.loadingText}>
            Loading prompt configuration...
          </div>
        </div>
      )}
      <TopBar onManageContentClick={() => setIsManageContentOpen(true)} />
      <div className={styles.messages}>
        <AnnouncementsBanner />
        <ConversationMessages
          isMessageLoading={isLoading}
          autoScrollEnabled={autoScrollEnabled}
          setAutoScrollEnabled={setAutoScrollEnabled}
          isEditMessageActive={isEditMessageActive}
          setIsEditMessageActive={setIsEditMessageActive}
          setPromptToEdit={setPromptToEdit}
          onOpenGallery={() => setIsGalleryModalOpen(true)}
          onUseTemplate={handleUseTemplate}
        />
      </div>
      <TemplateGalleryModal
        isOpen={isGalleryModalOpen}
        onClose={() => setIsGalleryModalOpen(false)}
        onUseTemplate={handleUseTemplate}
      />
      <div className={isMobile ? styles.messageBarMobile : styles.messageBar}>
        <ManageContent
          isOpen={isManageContentOpen}
          isPersonalWorkspace={isPersonalWorkspace}
          onClose={() => setIsManageContentOpen(false)}
          knowledgeComponents={allKnowledgeComponents}
          selectedKnowledgeComponents={currentTags}
          onKnowledgeComponentsChange={(tags: ITag[]) => {
            dispatch(
              setTagsForKey({
                key: conversationKey,
                tags: tags,
              }),
            );
          }}
          workspaceDocuments={allWorkspaceDocuments}
          selectedWorkspaceDocuments={currentLibraryDocuments}
          onWorkspaceDocumentsChange={(docs: IWorkspaceLibraryDocument[]) => {
            dispatch(clearLibraryDocumentsForKey(conversationKey));
            docs.forEach((doc) => {
              dispatch(
                addLibraryDocumentForKey({
                  key: conversationKey,
                  document: doc,
                }),
              );
            });
          }}
          uploadedDocuments={documentOptions}
          onUploadedDocumentsChange={(files: IFile[]) => {
            // Handle uploaded documents change
            // This would need to be implemented based on your requirements
          }}
          availableConversations={availableConversations}
          selectedConversations={referencedConversations}
          onConversationsChange={(conversations: IConversation[]) => {
            dispatch(setReferencedConversations(conversations));
          }}
          workspaceId={activeWorkspace?.id}
          onWorkspaceDocumentsRefresh={fetchWorkspaceDocuments}
          isLoadingKnowledgeComponents={isLoadingKnowledgeComponents}
          isLoadingWorkspaceDocuments={isLoadingWorkspaceDocuments}
          isLoadingConversations={isLoadingConversations}
        />

        <MessageBar
          setIsMessageLoading={setIsLoading}
          setAutoScrollEnabled={setAutoScrollEnabled}
          isEditMessageActive={isEditMessageActive}
          setIsEditMessageActive={setIsEditMessageActive}
          promptToEdit={promptToEdit}
          setPromptToEdit={setPromptToEdit}
        />
      </div>
    </div>
  );
};
